// ReliabilitySections.tsx · v12.10.17 (#1728 NumberField checks on blur, no per-keystroke clamp) · 2026-10-16 · VoiceSpectrum AI
// ReliabilitySections.tsx — v12.10.5 (#1121): Failure message gets the language tab bar (same as AI Agent) and, per language, the apology + voicemail line beside the failure message - both editable, both pre-recorded.
// ReliabilitySections.tsx — v12.7.1
// v12.7.1 (#590 / BDR-014): THIS SCREEN NO LONGER GOVERNS CONNECTED SYSTEMS, AND NO LONGER SAYS IT DOES.
//   12.7.0 moved the connected-system action timeout into `integration_settings.cs_action_timeout_ms` and
//   taught the engine to read it - but left this screen claiming its own Action timeout covered "any engine
//   action, including an outbound call to a connected system", and left the #515 hold-budget note here
//   computing a caller's wait from a number the engine had stopped using for those calls. Both are gone.
//   What remains is honest and narrower: this timeout is for OUR providers - LLM, TTS, STT, KB - which are
//   not held behind ConnectHoldStore, so there is no budget to show. The budget lives on Integrations, where
//   both of its inputs now are. BDR-014 §7: "both cross-links come out."
// v12.4.16 (#515): Action timeout now says what it MEANS against the call. The engine half of #515 moved
//   integration work out of the Twilio turn (start the action, say a filler, <Pause>, <Redirect>), so a long
//   timeout no longer drops a call - it decides how long a caller can be held in the hold loop. The two
//   settings that decide that total lived on two different screens and neither showed the combined effect,
//   which is half of what #515 is; that note computed it live from BOTH. SUPERSEDED BY #590 - the two
//   settings are one screen's now, and lib/holdBudget.ts is imported only by Integrations.
// ReliabilitySections.tsx — v12.1.14.0
// Reliability settings area (3 sections): Provider & Processing Reliability · Platform Connectivity ·
// External Systems (pointer-only). Real GET load + per-section Save/Discard wired to
// ReliabilitySettingsController, reusing the shared pick-one pill (settings-controls.css), the number
// field, and HelpTooltip. Ported for fidelity from reliability_only_v1_58.html. All fields apply live.
// Values persist here; CallEngine consumption is a separate deferred deliverable (backlog RE1).
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { HelpTooltip } from '../components/aiagent/HelpTooltip';
// v12.9.21 (#677/#845): the failure message is caller-facing speech, so it uses the SAME control as
// every other spoken sentence - language tabs, PREVIEW, Play, Auto-translate and the machine-drafted
// mark - instead of a bare textarea. His report, 2026-09-24: "reliability has no formatting as per our
// standard, look at other boxes ... it should match our shared component for prompt editor along with
// supported languages."
import { PromptEditor, langLabel } from '../components/PromptEditor';   // v12.10.5: langLabel for the tab names
// v12.4.16 (#515): ONE copy of ConnectHoldStore.BudgetFor's arithmetic, shared with Integrations › Failure
// policy so the two screens can never disagree about the same number.
// v12.7.1 (#590): lib/holdBudget is no longer imported here - the hold budget belongs to Integrations.
import './reliability.css';

export interface ReliabilityData {
  retryInitial: number; retryBackoff: string; retryMax: number; actionTimeout: number;
  terminalAction: string; offscriptTol: number; offscriptAction: string;
  engineApiTimeout: number; engineRetryMax: number; engineTerminalFailure: string;
  handoffEnabled: boolean;
  // v12.8.1 (#674): the message the two selectors above have named since v12.1.14.0 without it existing.
  // Keyed by language code; only the languages a tenant has edited are present.
  failureMessages: Record<string, string>;
  failureMessageDefault: string;
  // v12.9.21 (#862): language code -> the platform's default for THAT language. The single string above
  // was used as the placeholder behind every language box, so the Spanish box offered English.
  failureMessageDefaults?: Record<string, string>;
  // v12.10.5 (#1121): the apology + voicemail line per language (edited languages only) and the platform's.
  apologies?: Record<string, string>;
  apologyDefaults?: Record<string, string>;
  languages: string[];
}

// v12.10.5 (#1121): the tab label - the language's name in the reader's locale (same as AI Agent's tabs).
function langTabName(code: string): string {
  try { return new Intl.DisplayNames([typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en'], { type: 'language' }).of(code) || code; }
  catch { return code; }
}

export type ReliabilitySectionKey = 'provider' | 'platform' | 'failureMessage';   // v12.8.1 (#674)
export type ReliabilityDirtyMap = Record<ReliabilitySectionKey, boolean>;
export interface ReliabilityState { dirty: ReliabilityDirtyMap; anyDirty: boolean; saveAll: () => void; discardAll: () => void; }

const QKEY = ['t2-reliability'];

const BACKOFF: [string, string][] = [['exponential', 'Exponential'], ['constant', 'Constant'], ['none', 'None']];
const TERMINAL_ACTION: [string, string][] = [['voicemail', 'Route to voicemail'], ['handoff', 'Handoff'], ['end_msg', 'End with message']];
const OFFSCRIPT_ACTION: [string, string][] = [['reanchor', 'Re-anchor with prompt'], ['handoff', 'Route to handoff'], ['voicemail', 'Route to voicemail']];
const ENGINE_TERMINAL: [string, string][] = [['platform_error', 'Play platform-error message and end'], ['handoff', 'Route to handoff line'], ['voicemail', 'Route to voicemail']];

const PROVIDER_KEYS: (keyof ReliabilityData)[] = ['retryInitial', 'retryBackoff', 'retryMax', 'actionTimeout', 'terminalAction', 'offscriptTol', 'offscriptAction'];
const PLATFORM_KEYS: (keyof ReliabilityData)[] = ['engineApiTimeout', 'engineRetryMax', 'engineTerminalFailure'];
const MESSAGE_KEYS: (keyof ReliabilityData)[] = ['failureMessages', 'apologies'];   // v12.8.1 (#674); v12.10.5 (#1121)

export default function ReliabilitySections({ onStateChange }: { onStateChange?: (s: ReliabilityState) => void } = {}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<ReliabilityData>({
    queryKey: QKEY,
    queryFn: () => api.get<ReliabilityData>('/t2/settings/reliability'),
  });

  // v12.7.1 (#590 / BDR-014): THE CROSS-SCREEN READ IS GONE, and so is the hold-budget note it fed.
  // #515 put that note on both screens because the caller's hold budget multiplied a number from here by a
  // number from Integrations. BDR-014 ended that: the connected-system timeout is Integrations' own field
  // now, and this screen's Action timeout governs OUR providers - the LLM, TTS, STT and KB chain - which are
  // not held behind ConnectHoldStore at all. Keeping the note here would have described a budget this number
  // no longer takes part in.

  const [saved, setSaved] = useState<ReliabilityData | null>(null);
  const [draft, setDraft] = useState<ReliabilityData | null>(null);

  useEffect(() => {
    if (!data) return;
    setSaved(structuredClone(data));
    setDraft(structuredClone(data));
  }, [data]);

  function onSaved(fresh: ReliabilityData) {
    setSaved(structuredClone(fresh));
    setDraft(structuredClone(fresh));
    queryClient.setQueryData(QKEY, fresh);
  }

  const saveProvider = useMutation({
    mutationFn: (d: ReliabilityData) => api.put<ReliabilityData>('/t2/settings/reliability/provider-processing', {
      retryInitial: d.retryInitial, retryBackoff: d.retryBackoff, retryMax: d.retryMax, actionTimeout: d.actionTimeout,
      terminalAction: d.terminalAction, offscriptTol: d.offscriptTol, offscriptAction: d.offscriptAction,
    }),
    onSuccess: onSaved,
  });
  const savePlatform = useMutation({
    mutationFn: (d: ReliabilityData) => api.put<ReliabilityData>('/t2/settings/reliability/platform-connectivity', {
      engineApiTimeout: d.engineApiTimeout, engineRetryMax: d.engineRetryMax, engineTerminalFailure: d.engineTerminalFailure,
    }),
    onSuccess: onSaved,
  });
  // v12.8.1 (#674): saving also PRE-RENDERS the message to audio, server-side, in the tenant's own voice -
  // the trap plays when synthesis is unavailable, so it cannot be synthesized at the moment it is needed.
  const saveFailureMessage = useMutation({
    mutationFn: (d: ReliabilityData) => api.put<ReliabilityData>('/t2/settings/reliability/failure-message', {
      messages: d.failureMessages,
      apologies: d.apologies ?? {},   // v12.10.5 (#1121)
    }),
    onSuccess: onSaved,
  });

  const [msgTab, setMsgTab] = useState('');   // v12.10.5 (#1121): the language tab open in Failure message
  const reportRef = useRef(onStateChange);
  reportRef.current = onStateChange;

  useEffect(() => {
    if (!draft || !saved || !reportRef.current) return;
    const eqx = (keys: (keyof ReliabilityData)[]) => keys.every((k) => JSON.stringify(draft[k]) === JSON.stringify(saved[k]));
    const dirty: ReliabilityDirtyMap = { provider: !eqx(PROVIDER_KEYS), platform: !eqx(PLATFORM_KEYS), failureMessage: !eqx(MESSAGE_KEYS) };
    reportRef.current({
      dirty,
      anyDirty: dirty.provider || dirty.platform || dirty.failureMessage,
      saveAll: async () => {
        try {
          if (dirty.provider) await saveProvider.mutateAsync(draft);
          if (dirty.platform) await savePlatform.mutateAsync(draft);
          if (dirty.failureMessage) await saveFailureMessage.mutateAsync(draft);
        } catch { /* mutation error surfaces the failure */ }
      },
      discardAll: () => setDraft(saved ? structuredClone(saved) : draft),
    });
  }, [draft, saved]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !draft || !saved) return <div className="settings-loading">Loading Reliability settings…</div>;
  if (error) return <div className="settings-error">Reliability settings could not be loaded.</div>;

  const eq = (keys: (keyof ReliabilityData)[]) => keys.every((k) => JSON.stringify(draft[k]) === JSON.stringify(saved[k]));
  const patch = (p: Partial<ReliabilityData>) => setDraft((d) => (d ? { ...d, ...p } : d));
  const reset = (keys: (keyof ReliabilityData)[]) => setDraft((d) => {
    if (!d) return d;
    const next = { ...d };
    keys.forEach((k) => { (next as unknown as Record<string, unknown>)[k] = structuredClone((saved as unknown as Record<string, unknown>)[k]); });
    return next;
  });

  const providerDirty = !eq(PROVIDER_KEYS);
  const platformDirty = !eq(PLATFORM_KEYS);
  const messageDirty = !eq(MESSAGE_KEYS);
  // Languages the tenant actually speaks; 'en' is always offered so a single-language tenant sees one box.
  const msgLangs = (draft.languages?.length ? draft.languages : ['en'])
    .map(l => (l || '').split(/[-_]/)[0].toLowerCase()).filter((l, i, a) => l && a.indexOf(l) === i);
  const handoffOff = !draft.handoffEnabled;
  const curLang = msgLangs.includes(msgTab) ? msgTab : msgLangs[0];   // v12.10.5 (#1121)

  return (
    <div className="organization-panel reliability-panel">
      {/* ── 1. Provider & Processing Reliability ──────────────────────────── */}
      <Section title="Provider & Processing Reliability" description="How the assistant recovers when an AI provider or in-call processing step fails mid-call — the per-action retry chain and its terminal fallback."
        dirty={providerDirty} saving={saveProvider.isPending}
        onSave={() => saveProvider.mutate(draft)} onDiscard={() => reset(PROVIDER_KEYS)}>
        <div className="subhead">Resilience policies</div>
        <p className="sub-note">Organization-level error handling for in-call actions (LLM, TTS, STT, KB lookup), applied to every call across all workflows.</p>
        <div className="fr three">
          <Field label="Retry — initial delay" help={[['What it does', 'Starting wait before the first retry.'], ['Example', '500 ms with Exponential → retry #1 at 500ms, #2 at 1000ms.']]}>
            <NumberField value={draft.retryInitial} onChange={(v) => patch({ retryInitial: v })} unit="ms" min={0} max={5000} step={100} secHint />
          </Field>
          <Field label="Retry — backoff" help={[['What it does', 'How wait time grows between retries.'], ['Example', 'Exponential from 500ms → 500, 1000, 2000ms. Constant = same each time. None = immediate.']]}>
            <Select value={draft.retryBackoff} onChange={(v) => patch({ retryBackoff: v })} options={BACKOFF} />
          </Field>
          <Field label="Retry — max attempts" help={[['What it does', 'Maximum times the engine retries a failed action before the terminal failure action.'], ['Example', 'Set to 2 → LLM call fails → retry #1, retry #2 → if both fail, terminal failure action runs.']]}>
            <NumberField value={draft.retryMax} onChange={(v) => patch({ retryMax: v })} min={0} max={5} step={1} />
          </Field>
        </div>
        <div className="fr three">
          {/* v12.7.1 (#590 / BDR-014): this help used to end "including an outbound call to a connected system". After 12.7.0 that was false. */}
          <Field label="Action timeout" help={[['What it does', 'How long to wait for one of OUR OWN in-call actions before counting it as failed — an LLM, TTS, STT or knowledge-base call.'], ['What it does NOT do', 'It no longer has any effect on an outbound call to a connected system. That timeout is set in Integrations → Failure policy, on the same screen as the retries and the caller fallback.']]}>
            <NumberField value={draft.actionTimeout} onChange={(v) => patch({ actionTimeout: v })} unit="ms" min={500} max={30000} step={500} secHint />
          </Field>
          <Field label="Terminal failure action" help={[['What it does', 'Final fallback when retries are exhausted.'], ['Example', 'Route to voicemail = apologize and offer voicemail. Handoff = transfer. End with message = polite close.']]}>
            <Select value={draft.terminalAction} onChange={(v) => patch({ terminalAction: v })} options={TERMINAL_ACTION} handoffOff={handoffOff} />
          </Field>
          <Field label="Off-script tolerance" help={[['What it does', 'How many off-topic caller turns are tolerated before the AI re-anchors or escalates.'], ['Example', 'Set to 2 → after two off-topic turns the AI re-anchors with a clarifying prompt.']]}>
            <NumberField value={draft.offscriptTol} onChange={(v) => patch({ offscriptTol: v })} min={1} max={5} step={1} />
          </Field>
        </div>
        <div className="fr">
          <Field label="Off-script action" help={[['What it does', 'What to do when off-script tolerance is exceeded.'], ['Example', 'Re-anchor with prompt = gentle redirect. Route to handoff = transfer. Route to voicemail = polite exit.']]}>
            <Select value={draft.offscriptAction} onChange={(v) => patch({ offscriptAction: v })} options={OFFSCRIPT_ACTION} handoffOff={handoffOff} />
          </Field>
          <div className="f" />
        </div>
        <div className="xref-note" style={{ marginTop: 20 }}>
          <LinkIcon />
          <div>Failures of <em>external connected systems</em> (EHR, scheduling, webhooks) — what the assistant does when a third-party system is unreachable — are governed in <em>Integrations → Failure policy</em>, and summarized under <em>External Systems</em> below. This section covers only the AI provider &amp; in-call processing retries.</div>
        </div>
      </Section>

      {/* ── 2. Platform Connectivity ──────────────────────────────────────── */}
      <Section title="Platform Connectivity" description="How the tenant's call engine reaches VoiceSpectrum platform services, and what callers hear if it can't."
        dirty={platformDirty} saving={savePlatform.isPending}
        onSave={() => savePlatform.mutate(draft)} onDiscard={() => reset(PLATFORM_KEYS)}>
        <div className="xref-note">
          <ServerIcon />
          <div>This is the <em>platform-API</em> retry layer — the engine talking to VoiceSpectrum services (auth, language packs, config). It is separate from the per-action retries in <em>Provider &amp; Processing Reliability</em> above, which govern individual LLM, TTS, and lookup calls within a turn.</div>
        </div>
        <div className="fr three" style={{ marginTop: 18 }}>
          <Field label="Engine API timeout" help={[['What it does', 'How long the engine waits for a VoiceSpectrum platform service to respond before counting the request as failed.'], ['Example', 'Set to 5000 ms → a config fetch that takes 6s is marked failed and the retry chain begins.']]}>
            <NumberField value={draft.engineApiTimeout} onChange={(v) => patch({ engineApiTimeout: v })} unit="ms" min={500} max={30000} step={500} secHint />
          </Field>
          <Field label="Retry max" help={[['What it does', 'Maximum retry attempts on a platform-API failure before the terminal failure action runs.'], ['Example', 'Set to 3 → two retries eventually succeed → the call continues. All three fail → terminal failure action fires.']]}>
            <NumberField value={draft.engineRetryMax} onChange={(v) => patch({ engineRetryMax: v })} min={0} max={10} step={1} />
          </Field>
          <Field label="On terminal failure" help={[['What it does', 'What the caller experiences when the engine cannot reach the platform at all (auth, pack, or config unavailable).'], ['Example', 'Play platform-error message and end = a polite “system issue” close. Route to handoff line = transfer to the front desk. Route to voicemail = capture the message.']]}>
            <Select value={draft.engineTerminalFailure} onChange={(v) => patch({ engineTerminalFailure: v })} options={ENGINE_TERMINAL} handoffOff={handoffOff} />
          </Field>
        </div>
      </Section>

      {/* ── v12.8.1 (#674): THE FAILURE MESSAGE. ──────────────────────────────
          It sits here, directly under the two selectors that name it, because it IS what they refer to:
          "End with message" above, and "Play platform-error message and end" in Platform Connectivity. Both
          have offered a message since v12.1.14.0 and there has never been one - no column, no field, no
          default, nothing in the engine. On 2026-09-24 a caller met the gap and heard Twilio's own
          "An application error has occurred. Goodbye!" instead (#668).

          Per language, and only the languages the tenant speaks - the same shape as the voicemail greeting
          and the handoff busy message. Tushar's call: "should we keep support for other languages as we do?" */}
      <Section title="Failure message" description="What a caller hears, in your own voice, when the assistant cannot speak or reach the platform - the message before the call ends, and the apology before voicemail."
        dirty={messageDirty} saving={saveFailureMessage.isPending}
        onSave={() => saveFailureMessage.mutate(draft)} onDiscard={() => reset(MESSAGE_KEYS)}>
        <div className="xref-note">
          <ServerIcon />
          <div>
            The first sentence is the message behind <em>End with message</em> and <em>Play platform-error message and end</em>;
            the second is what a caller hears when the voice fails mid-call, before they leave a voicemail.
            It is <b>recorded in your agent&rsquo;s own voice when you save</b>, not at the moment it is needed &mdash;
            the whole point is that it still plays when the voice service is unavailable. Changing your agent&rsquo;s
            voice re-records them automatically. If you clear a language, that language falls back to the next one
            you have set; it never becomes silence.
          </div>
        </div>
        {/* v12.10.5 (#1121): the language tab bar every other spoken sentence uses; per language, the two sentences. */}
        <div className="lt-bar" style={{ marginTop: 14 }}><div className="lt-tabs">
          {msgLangs.map((c) => (
            <button key={c} type="button" className={`lt-tab${c === curLang ? ' on' : ''}`} onClick={() => setMsgTab(c)}>{langLabel({ code: c, displayName: langTabName(c) })}</button>
          ))}
        </div></div>
        {[curLang].map((lang) => (
          <div key={lang}>
          <div className="fr" style={{ marginTop: 14 }}>
            <Field label="Failure message - the call ends"
              help={[['What it does', 'The last thing a caller hears when the assistant cannot continue. Spoken in your configured voice, then the call ends.'],
                     ['Keep it short', 'One or two sentences. Say that there is a problem, that someone will follow up, and that the call is ending - a caller who is not told the call is over will keep talking to a line that has hung up.']]}>
              {/* v12.9.21 (#677/#845). `noTokens` is deliberate: this sentence plays when the platform itself could not be
                  reached, so a token that must be resolved against platform data is the last thing it should contain.
                  #862: the placeholder is the platform default FOR THIS LANGUAGE. */}
              <PromptEditor
                value={draft.failureMessages?.[lang] ?? ''}
                onChange={(v) => patch({ failureMessages: { ...(draft.failureMessages ?? {}), [lang]: v } })}
                language={lang}
                rows={2}
                noTokens
                platformDefault={draft.failureMessageDefaults?.[lang] ?? draft.failureMessageDefault}
                translateSource={draft.failureMessages?.[msgLangs[0]] ?? ''}
                translateSourceLang={msgLangs[0]}
              />
            </Field>
          </div>
          <div className="fr" style={{ marginTop: 14 }}>
            <Field label="Apology + voicemail - the caller leaves a message"
              help={[['What it does', 'What a caller hears when the voice fails or a step breaks in the MIDDLE of a call: an apology, then the voicemail tone, so they can still leave a message.'],
                     ['When it fires', 'Mid-call only, when the assistant cannot carry on. Recorded in your agent’s voice when you save, so it still plays when the voice service is down.'],
                     ['Example', 'I’m sorry, we are having difficulties on our side. Please leave your name, your phone number and a short message after the tone, and we will call you back.']]}>
              <PromptEditor
                value={draft.apologies?.[lang] ?? ''}
                onChange={(v) => patch({ apologies: { ...(draft.apologies ?? {}), [lang]: v } })}
                language={lang}
                rows={2}
                noTokens
                platformDefault={draft.apologyDefaults?.[lang] ?? draft.apologyDefaults?.en ?? ''}
                translateSource={draft.apologies?.[msgLangs[0]] ?? ''}
                translateSourceLang={msgLangs[0]}
              />
            </Field>
          </div>
          </div>
        ))}
      </Section>

      {/* ── 3. External Systems (pointer-only) ────────────────────────────── */}
      <Section title="External Systems" description="What the assistant does when a connected third-party system (EHR, scheduling, CRM, webhooks) is slow or unreachable." pointerOnly>
        <div className="xref-note">
          <LinkIcon />
          <div><b>Everything about connected systems is configured in Integrations → Failure policy — including the timeout.</b> Open <em>Integrations → Failure policy</em> to set how long we wait on their endpoint, how many times we retry, and what the caller hears when it fails. Nothing on this screen affects those calls.</div>
        </div>
      </Section>
    </div>
  );
}

// ── Section chrome (shared Settings card; External Systems is a pointer with no Save/Discard) ──
function Section(props: { title: string; description: string; dirty?: boolean; saving?: boolean; onSave?: () => void; onDiscard?: () => void; pointerOnly?: boolean; children: ReactNode }) {
  return (
    <section className={`settings-section-card${props.dirty ? ' dirty' : ''}`}>
      <header className="settings-section-header">
        <div className="settings-section-headrow">
          <h2>{props.title}</h2>
          {!props.pointerOnly && (
            <div className="settings-actions">
              <button type="button" className="settings-secondary" disabled={!props.dirty || props.saving} onClick={props.onDiscard}>Discard</button>
              <button type="button" className="settings-primary" disabled={!props.dirty || props.saving} onClick={props.onSave}>{props.saving ? 'Saving…' : 'Save section'}</button>
            </div>
          )}
        </div>
        <p className="rel-sec-desc">{props.description}</p>
      </header>
      <div className="rel-section-body">{props.children}</div>
    </section>
  );
}

function Field({ label, help, children }: { label: string; help?: [string, ReactNode][]; children: ReactNode }) {
  return (
    <div className="f">
      <label>{label}{help && <HelpTooltip sections={help} />}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, handoffOff }: { value: string; onChange: (v: string) => void; options: [string, string][]; handoffOff?: boolean }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, l]) => (
        <option key={v} value={v} disabled={handoffOff && v === 'handoff'}>{l}{handoffOff && v === 'handoff' ? ' (Handoff off)' : ''}</option>
      ))}
    </select>
  );
}

function NumberField({ value, onChange, unit, min, max, step = 1, secHint }: {
  value: number; onChange: (v: number) => void; unit?: string; min: number; max: number; step?: number; secHint?: boolean;
}) {
  const clamp = (v: number) => (v < min ? min : v > max ? max : v);
  // v12.10.17 (#1728): typing is free (no clamp per keystroke); an in-range number reaches the draft as typed, an out-of-range
  // or empty box keeps the last good value and, on leaving the box, says the allowed range instead of jumping. Steppers clamp as before.
  const [text, setText] = useState(String(value));
  const [bad, setBad] = useState(false);
  const inRange = (t: string) => t.trim() !== '' && Number.isFinite(Number(t)) && Number(t) >= min && Number(t) <= max;
  useEffect(() => { setText((t) => (inRange(t) && Number(t) === Number(value) ? t : String(value))); setBad(false); }, [value]);   // eslint-disable-line react-hooks/exhaustive-deps
  const bump = (dir: number) => onChange(clamp((Number(value) || 0) + dir * step));
  return (
    <>
      {secHint && <span className="nf-sec-top">{msToSec(value)}</span>}
      <div className="numfield">
        <input type="text" inputMode="numeric" className={`nf-input${bad ? ' nf-bad' : ''}`} value={text} aria-invalid={bad || undefined}
          onChange={(e) => { const t = e.target.value.replace(/[^0-9.-]/g, ''); setText(t); if (inRange(t)) { setBad(false); if (Number(t) !== Number(value)) onChange(Number(t)); } }}
          onBlur={() => setBad(!inRange(text))}
          onKeyDown={(e) => { if (e.key === 'Enter') setBad(!inRange(text)); }} />
        <div className="nf-labels">{unit && <span className="nf-unit">{unit}</span>}</div>
        <div className="nf-steppers">
          <button type="button" className="nf-up" onClick={() => bump(1)} aria-label="Increase" tabIndex={-1}><Chevron up /></button>
          <button type="button" className="nf-down" onClick={() => bump(-1)} aria-label="Decrease" tabIndex={-1}><Chevron /></button>
        </div>
      </div>
      {bad && <small className="nf-range" role="alert">Allowed: {min}&ndash;{max}{unit ? ` ${unit}` : ''}</small>}{/* v12.10.17 (#1728) */}
    </>
  );
}

function msToSec(ms: number | string): string {
  const n = Number(ms);
  if (!isFinite(n)) return '';
  const s = n / 1000;
  return (Number.isInteger(s) ? s : s.toFixed(s < 1 ? 2 : 1)) + ' sec';
}
function Chevron({ up }: { up?: boolean }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{up ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}</svg>;
}
function LinkIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg>; }
// v12.4.16 (#515): the hold budget is about elapsed time, so it gets a clock rather than the link glyph.
// v12.7.1 (#590): ClockIcon went with the hold-budget note - it was that note's glyph and nothing else used it.
function ServerIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2" /><rect x="2" y="14" width="20" height="8" rx="2" /><path d="M6 6h.01M6 18h.01" /></svg>; }
